import { useState, useEffect } from 'react'
import { Search } from 'lucide-react'
import { GerencialAdvancedFiltersPopover, type AdvancedFilters } from './GerencialAdvancedFiltersPopover'
import { toDateInputValue, parseDateInputValue } from '../utils/dateUtils'

export interface GerencialFilterState {
  dataInicial: Date
  dataFinal: Date
  codigoCliente?: string
  natureza?: string
  centroResultado?: string
  empresa?: string
  centroCusto?: string
  search?: string
  valorMin?: number
  valorMax?: number
}

interface ClientOption {
  codigo: string
  nome: string
}

interface GerencialFiltersBarProps {
  value: GerencialFilterState
  onChange: (next: GerencialFilterState) => void
  clients: ClientOption[]
  /** Distinct Natureza values seen on the current page — used as datalist suggestions. */
  naturezaSuggestions?: string[]
  centroResultados?: string[]
  compact?: boolean
}

/**
 * Filters toolbar — sticky at the top of the page. In compact mode (used by
 * edit mode) it hides the chips line; in full mode the chips render above the
 * table via `<GerencialFilterChips />` (rendered separately by the page).
 */
export function GerencialFiltersBar({
  value,
  onChange,
  clients,
  naturezaSuggestions,
  centroResultados,
  compact,
}: GerencialFiltersBarProps) {
  const [searchDraft, setSearchDraft] = useState(value.search ?? '')
  const [naturezaDraft, setNaturezaDraft] = useState(value.natureza ?? '')

  // Sync local drafts when parent values change externally
  // (e.g., after a "Manter alterações" rollback or chip removal).
  useEffect(() => {
    setSearchDraft(value.search ?? '')
  }, [value.search])
  useEffect(() => {
    setNaturezaDraft(value.natureza ?? '')
  }, [value.natureza])

  const update = (patch: Partial<GerencialFilterState>) => {
    onChange({ ...value, ...patch })
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      update({ search: searchDraft.trim() || undefined })
    }
  }

  const commitNatureza = () => {
    const trimmed = naturezaDraft.trim()
    if ((trimmed || undefined) !== value.natureza) {
      update({ natureza: trimmed || undefined })
    }
  }

  const onAdvancedApply = (advanced: AdvancedFilters) => {
    update({
      empresa: advanced.empresa,
      centroCusto: advanced.centroCusto,
      valorMin: advanced.valorMin,
      valorMax: advanced.valorMax,
    })
  }

  return (
    <div className={`p-4 bg-brand-dark-light rounded-monumenta border border-brand-dark-lighter ${compact ? 'sticky top-0 z-10' : ''}`}>
      <div className="flex items-center gap-3 flex-wrap">
        {/* Period */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-brand-gray uppercase tracking-wide">
            Período
          </span>
          <input
            type="date"
            value={toDateInputValue(value.dataInicial)}
            onChange={e => {
              const d = parseDateInputValue(e.target.value)
              if (d) update({ dataInicial: d })
            }}
            className="bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
          <span className="text-brand-gray">→</span>
          <input
            type="date"
            value={toDateInputValue(value.dataFinal)}
            onChange={e => {
              const d = parseDateInputValue(e.target.value)
              if (d) update({ dataFinal: d })
            }}
            className="bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>

        {/* Cliente */}
        <select
          value={value.codigoCliente ?? ''}
          onChange={e => update({ codigoCliente: e.target.value || undefined })}
          className="bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary min-w-[140px]"
        >
          <option value="">Todos os clientes</option>
          {clients.map(c => (
            <option key={c.codigo} value={c.codigo}>{c.nome}</option>
          ))}
        </select>

        {/* Natureza — free-text input with autocomplete from page values.
            Natureza is a free-form code/string in the Monumenta domain (e.g.
            "31006"), not a fixed enum, so a select would be wrong. */}
        <input
          type="text"
          list="gerencial-natureza-suggestions"
          value={naturezaDraft}
          onChange={e => setNaturezaDraft(e.target.value)}
          onBlur={commitNatureza}
          onKeyDown={e => { if (e.key === 'Enter') commitNatureza() }}
          placeholder="Natureza"
          className="bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary w-32"
        />
        {naturezaSuggestions && naturezaSuggestions.length > 0 && (
          <datalist id="gerencial-natureza-suggestions">
            {naturezaSuggestions.map(n => <option key={n} value={n} />)}
          </datalist>
        )}

        {/* Centro de Resultado */}
        {centroResultados && centroResultados.length > 0 && (
          <select
            value={value.centroResultado ?? ''}
            onChange={e => update({ centroResultado: e.target.value || undefined })}
            className="bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
          >
            <option value="">Todos C. Resultado</option>
            {centroResultados.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        )}

        {/* Search */}
        <div className="flex items-center gap-1 flex-1 min-w-[200px]">
          <Search size={14} className="text-brand-gray" />
          <input
            type="text"
            value={searchDraft}
            onChange={e => setSearchDraft(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onBlur={() => {
              if ((searchDraft.trim() || undefined) !== value.search) {
                update({ search: searchDraft.trim() || undefined })
              }
            }}
            placeholder="Buscar memo, nota, job, cliente…"
            className="flex-1 bg-brand-dark-lighter border border-brand-dark-lighter rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
          />
        </div>

        {/* Advanced */}
        <GerencialAdvancedFiltersPopover
          value={{
            empresa: value.empresa,
            centroCusto: value.centroCusto,
            valorMin: value.valorMin,
            valorMax: value.valorMax,
          }}
          onApply={onAdvancedApply}
        />
      </div>
    </div>
  )
}
